import apiClient from '../client';
import type { BrandSchema } from '@appTypes';

export type { BrandSchema } from '@appTypes';

/**
 * Fetch all brands (non-paginated)
 */
export async function fetchBrands(): Promise<{ [key: string]: BrandSchema } | undefined> {
  const response = await apiClient.get('/admin/brands', {
    params: { paginated: false },
  });
  return response.data;
}

/**
 * Fetch paginated brands
 */
export async function fetchPaginatedBrands(params?: { lastDocumentId?: string }): Promise<{ data: { [key: string]: BrandSchema }; lastDocumentId?: string } | undefined> {
  const response: any = await apiClient.get('/admin/brands', {
    params: { paginated: true, lastDocumentId: params?.lastDocumentId },
  });
  return { data: response.data, lastDocumentId: response.lastDocumentId };
}

/**
 * Get a specific brand by ID
 */
export async function getBrandById(params: { brandId: string }): Promise<BrandSchema | undefined> {
  const response = await apiClient.get(`/admin/brands/${params.brandId}`);
  return response.data;
}

/**
 * Create a new brand
 */
export async function addBrand(params: {
  brandDescription: { [key: string]: string };
  brandName: { [key: string]: string };
  brandLogoURL: string;
}): Promise<BrandSchema> {
  const response = await apiClient.post('/admin/brands', params);
  return response.data;
}

/**
 * Update an existing brand
 */
export async function updateBrand(params: {
  brand: BrandSchema;
  newData?: Partial<BrandSchema>;
}): Promise<BrandSchema> {
  const brandId = params.brand.brandId;
  const updateData = params.newData || params.brand;

  const response = await apiClient.put(`/admin/brands/${brandId}`, updateData);
  return response.data;
}

/**
 * Delete a brand
 */
export async function deleteBrand(params: { brand: BrandSchema }): Promise<void> {
  const brandId = params.brand.brandId;
  await apiClient.delete(`/admin/brands/${brandId}`);
}

/**
 * Check if a brand can be deleted
 */
export async function canDeleteBrand(params: { brand: BrandSchema }): Promise<boolean> {
  const brandId = params.brand.brandId;
  const response = await apiClient.get(`/admin/brands/${brandId}/can-delete`);
  return response.data.canDelete;
}

/**
 * Toggle brand archive status
 */
export async function toggleArchiveBrand(params: { brand: BrandSchema }): Promise<boolean> {
  const brandId = params.brand.brandId;
  const response = await apiClient.post(`/admin/brands/${brandId}/archive`);
  return response.data.isArchived;
}
